<template>
  <div class="page">
    <section class="card">
      <p class="eyebrow">{{ t('submitted') }}</p>
      <h1>{{ t('thanks') }}</h1>
      <div v-if="itemData" class="item-summary">
        <div v-if="itemData.coverImageUrl" class="item-image">
          <img :src="itemData.coverImageUrl" :alt="itemData.title" />
        </div>
        <div>
          <p class="item-title"><strong>{{ itemData.title }}</strong></p>
          <p v-if="itemData.description" class="item-description">{{ itemData.description }}</p>
          <div v-if="itemData.socialLinks?.length" class="item-socials">
            <a
              v-for="link in itemData.socialLinks"
              :key="link"
              class="social-link"
              :href="link"
              target="_blank"
              rel="noopener"
            >
              <span class="social-icon" :class="getSocialType(link)">
                <span v-if="getSocialType(link) === 'instagram'">
                  <img src="/icons/instagram.png" alt="Instagram" width="14" height="14" />
                </span>
                <span v-else-if="getSocialType(link) === 'tiktok'">
                  <img src="/icons/tiktok.png" alt="TikTok" width="14" height="14" />
                </span>
                <span v-else-if="getSocialType(link) === 'twitter'">
                  <img src="/icons/twitter.png" alt="Twitter" width="14" height="14" />
                </span>
                <span v-else-if="getSocialType(link) === 'pinterest'">
                  <img src="/icons/pinterest.png" alt="Pinterest" width="14" height="14" />
                </span>
                <span v-else>
                  <span class="link-prefix">www</span>
                </span>
              </span>
            </a>
          </div>
        </div>
      </div>
      <p class="subtext">
        <strong>{{ itemTitle }}</strong>{{ t('responsesRecorded') }}
      </p>
      <div class="actions">
        <router-link class="primary" :to="eventLink">{{ t('backToEvent') }}</router-link>
      </div>
    </section>

    <section v-if="errorMessage" class="card error">{{ errorMessage }}</section>
  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { getItemByShortCode } from '../services/publicApi'
import { useI18n } from '../i18n'

export default {
  name: 'EventItemSubmittedPage',
  setup() {
    const route = useRoute()
    const itemTitle = ref('bu öğe')
    const itemData = ref(null)
    const errorMessage = ref('')
    const { t } = useI18n()

    const eventLink = {
      name: 'event',
      params: { eventSlug: route.params.eventSlug }
    }

    const questionsLink = {
      name: 'event-item-questions',
      params: {
        eventSlug: route.params.eventSlug,
        itemShortCode: route.params.itemShortCode
      }
    }

    const loadItem = async () => {
      try {
        const item = await getItemByShortCode(
          route.params.eventSlug,
          route.params.itemShortCode
        )
        itemTitle.value = item.title
        itemData.value = item
      } catch (error) {
        errorMessage.value = error.status === 404
          ? t('itemNotFound')
          : t('itemDetailsFailed')
      }
    }

    const getSocialType = (link) => {
      const lower = link.toLowerCase()
      if (lower.includes('instagram.com')) return 'instagram'
      if (lower.includes('tiktok.com')) return 'tiktok'
      if (lower.includes('twitter.com') || lower.includes('x.com')) return 'twitter'
      if (lower.includes('pinterest.com')) return 'pinterest'
      return 'www'
    }

    onMounted(loadItem)

    return {
      itemTitle,
      itemData,
      errorMessage,
      t,
      eventLink,
      questionsLink,
      getSocialType
    }
  }
}
</script>

<style scoped>
.page {
  display: grid;
  gap: 20px;
}

.card {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.eyebrow {
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #94a3b8;
  margin: 0 0 8px;
}

.subtext {
  color: #64748b;
  margin: 10px 0 0;
}

.actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.item-summary {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 16px;
  align-items: center;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  margin-top: 16px;
}

.item-image {
  width: 88px;
  height: 88px;
  border-radius: 18px;
  overflow: hidden;
  background: #e2e8f0;
  flex-shrink: 0;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.item-description {
  margin: 0 0 10px;
  color: #64748b;
  font-size: 14px;
}

.item-socials {
  display: flex;
  align-items: center;
  gap: 8px;
}

.social-link {
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.social-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #1e293b;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: #ffffff;
}

.social-icon.instagram,
.social-icon.tiktok,
.social-icon.twitter,
.social-icon.pinterest {
  background: #ffffff;
}

.primary,
.secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 12px;
  font-weight: 600;
  text-decoration: none;
}

.primary {
  background: #2563eb;
  color: #ffffff;
}

.secondary {
  background: #e2e8f0;
  color: #0f172a;
}

.error {
  color: #b91c1c;
  border: 1px solid #fecaca;
  background: #fff1f2;
}
</style>
